/**
 * TutorialManager - Simple 2-3 step tutorial for first-time players
 */

import { UI } from '../game/constants';

export interface TutorialStep {
  title: string;
  message: string;
  highlightSelector?: string; // CSS selector to highlight element
  position?: 'top' | 'bottom' | 'left' | 'right';
  action?: () => void; // Optional action to perform
}

export class TutorialManager {
  private currentStep: number = 0;
  private steps: TutorialStep[] = [];
  private isActive: boolean = false;
  private overlayElement: HTMLElement | null = null;
  private tooltipElement: HTMLElement | null = null;
  private hasCompletedTutorial: boolean = false;

  constructor() {
    this.hasCompletedTutorial = this.loadCompletionStatus();
    this.initializeTutorialSteps();
  }

  private loadCompletionStatus(): boolean {
    return localStorage.getItem('paperclips-tutorial-completed') === 'true';
  }

  private saveCompletionStatus() {
    localStorage.setItem('paperclips-tutorial-completed', 'true');
  }

  private initializeTutorialSteps() {
    this.steps = [
      {
        title: 'Welcome to Universal Paperclips!',
        message: 'Your goal is to make paperclips. Click the button below to make your first clip manually.',
        highlightSelector: '#make-clip-btn',
        position: 'bottom',
      },
      {
        title: 'Earn Funds & Automate',
        message: 'Unsold clips will sell automatically for funds. Use funds to buy AutoClippers for passive production!',
        highlightSelector: '#panel-business',
        position: 'top',
      },
      {
        title: 'Expand Your Operation',
        message: 'As you progress, unlock Computing and Projects to expand your capabilities. Good luck!',
        highlightSelector: '#panel-projects',
        position: 'top',
      },
    ];
  }

  shouldShowTutorial(): boolean {
    return !this.hasCompletedTutorial;
  }

  start() {
    if (this.hasCompletedTutorial || this.isActive) return;

    this.isActive = true;
    this.currentStep = 0;
    this.showStep(this.currentStep);
  }

  private showStep(stepIndex: number) {
    if (stepIndex >= this.steps.length) {
      this.complete();
      return;
    }

    const step = this.steps[stepIndex];
    if (!step) {
      this.complete();
      return;
    }

    // Create overlay
    this.createOverlay();

    // Highlight target element if specified
    if (step.highlightSelector) {
      this.highlightElement(step.highlightSelector);
    }

    // Show tooltip
    this.showTooltip(step);
  }

  private createOverlay() {
    // Remove existing overlay if any
    if (this.overlayElement) {
      this.overlayElement.remove();
    }

    this.overlayElement = document.createElement('div');
    this.overlayElement.id = 'tutorial-overlay';
    // Lighter overlay on mobile (50% opacity) for better visibility
    const isMobile = window.innerWidth <= UI.MOBILE_BREAKPOINT_PX;
    const overlayOpacity = isMobile ? UI.TUTORIAL_OVERLAY_OPACITY.MOBILE : UI.TUTORIAL_OVERLAY_OPACITY.DESKTOP;
    this.overlayElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, ${overlayOpacity});
      z-index: ${UI.Z_INDEX.OVERLAY};
      pointer-events: none;
    `;
    document.body.appendChild(this.overlayElement);
  }

  private highlightElement(selector: string) {
    const element = document.querySelector(selector) as HTMLElement;
    if (!element) return;

    // Make element clickable through overlay
    // IMPORTANT: Don't override position if it's already set via CSS (e.g., fixed on mobile)
    const currentPosition = window.getComputedStyle(element).position;
    if (currentPosition === 'static') {
      element.style.position = 'relative';
    }
    element.style.zIndex = UI.Z_INDEX.HIGHLIGHTED.toString();
    element.style.pointerEvents = 'auto';

    // Add highlight effect
    element.style.boxShadow = '0 0 20px 10px rgba(0, 255, 255, 0.6)';
    element.style.transition = 'box-shadow 0.3s ease';

    // Store original styles for cleanup
    element.dataset.tutorialHighlight = 'true';
  }

  private showTooltip(step: TutorialStep) {
    // Remove existing tooltip
    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }

    this.tooltipElement = document.createElement('div');
    this.tooltipElement.id = 'tutorial-tooltip';
    // Mobile-friendly sizing and positioning
    const isMobile = window.innerWidth <= UI.MOBILE_BREAKPOINT_PX;
    const maxWidth = isMobile ? '90%' : '400px';
    const padding = isMobile ? '16px' : '20px';
    this.tooltipElement.style.cssText = `
      position: fixed;
      background: var(--bg-primary, #0a0a0a);
      border: 2px solid var(--accent-cyan, #00ffff);
      padding: ${padding};
      border-radius: 8px;
      max-width: ${maxWidth};
      width: ${isMobile ? '90%' : 'auto'};
      z-index: ${UI.Z_INDEX.TOOLTIP};
      box-shadow: 0 4px 20px rgba(0, 255, 255, 0.3);
      pointer-events: auto;
      max-height: 80vh;
      overflow-y: auto;
    `;

    const html = `
      <h3 style="margin: 0 0 12px 0; color: var(--accent-cyan, #00ffff); font-size: 1.2em;">
        ${step.title}
      </h3>
      <p style="margin: 0 0 16px 0; color: var(--text-primary, #e0e0e0); line-height: 1.5;">
        ${step.message}
      </p>
      <div style="display: flex; gap: 10px; justify-content: flex-end;">
        ${this.currentStep > 0 ? '<button class="btn btn-secondary tutorial-btn" id="tutorial-prev">Previous</button>' : ''}
        ${this.currentStep < this.steps.length - 1 
          ? '<button class="btn btn-primary tutorial-btn" id="tutorial-next">Next</button>' 
          : '<button class="btn btn-primary tutorial-btn" id="tutorial-finish">Get Started!</button>'}
        <button class="btn btn-secondary tutorial-btn" id="tutorial-skip">Skip Tutorial</button>
      </div>
    `;

    this.tooltipElement.innerHTML = html;

    // Position tooltip
    this.positionTooltip(step);

    document.body.appendChild(this.tooltipElement);

    // Attach event listeners using proper event delegation
    const nextBtn = this.tooltipElement.querySelector('#tutorial-next');
    const prevBtn = this.tooltipElement.querySelector('#tutorial-prev');
    const finishBtn = this.tooltipElement.querySelector('#tutorial-finish');
    const skipBtn = this.tooltipElement.querySelector('#tutorial-skip');
    
    if (nextBtn) nextBtn.addEventListener('click', (e) => { e.stopPropagation(); this.next(); });
    if (prevBtn) prevBtn.addEventListener('click', (e) => { e.stopPropagation(); this.previous(); });
    if (finishBtn) finishBtn.addEventListener('click', (e) => { e.stopPropagation(); this.complete(); });
    if (skipBtn) skipBtn.addEventListener('click', (e) => { e.stopPropagation(); this.skip(); });
  }

  private positionTooltip(step: TutorialStep) {
    if (!this.tooltipElement) return;

    // On mobile, ALWAYS center the tooltip for maximum visibility and accessibility
    const isMobile = window.innerWidth <= UI.MOBILE_BREAKPOINT_PX;
    
    let top = '50%';
    let left = '50%';
    let transform = 'translate(-50%, -50%)';

    // Only use relative positioning on desktop
    if (!isMobile && step.highlightSelector && step.position) {
      const element = document.querySelector(step.highlightSelector);
      if (element) {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        switch (step.position) {
          case 'bottom':
            // Position below element if there's room, otherwise center
            if (rect.bottom + 250 < viewportHeight) {
              top = `${rect.bottom + 20}px`;
              left = `${rect.left + rect.width / 2}px`;
              transform = 'translateX(-50%)';
            }
            break;
          case 'top':
            // Position above element if there's room, otherwise center
            if (rect.top > 250) {
              top = `${rect.top - 20}px`;
              left = `${rect.left + rect.width / 2}px`;
              transform = 'translate(-50%, -100%)';
            }
            break;
          case 'left':
            if (rect.left > 420) {
              top = `${rect.top + rect.height / 2}px`;
              left = `${rect.left - 20}px`;
              transform = 'translate(-100%, -50%)';
            }
            break;
          case 'right':
            if (rect.right + 420 < window.innerWidth) {
              top = `${rect.top + rect.height / 2}px`;
              left = `${rect.right + 20}px`;
              transform = 'translateY(-50%)';
            }
            break;
        }
      }
    }

    this.tooltipElement.style.top = top;
    this.tooltipElement.style.left = left;
    this.tooltipElement.style.transform = transform;
  }

  next() {
    this.clearHighlights();
    this.currentStep++;
    this.showStep(this.currentStep);
  }

  previous() {
    this.clearHighlights();
    this.currentStep = Math.max(0, this.currentStep - 1);
    this.showStep(this.currentStep);
  }

  complete() {
    this.hasCompletedTutorial = true;
    this.saveCompletionStatus();
    this.end();
  }

  skip() {
    this.complete();
  }

  private clearHighlights() {
    // Remove highlights from all elements
    document.querySelectorAll('[data-tutorial-highlight="true"]').forEach((el) => {
      const element = el as HTMLElement;
      element.style.boxShadow = '';
      element.style.zIndex = '';
      element.style.pointerEvents = '';
      element.style.position = ''; // Clear inline position to allow CSS rules to apply
      element.style.transition = '';
      delete element.dataset.tutorialHighlight;
    });
  }

  private end() {
    this.isActive = false;
    this.clearHighlights();

    if (this.overlayElement) {
      this.overlayElement.remove();
      this.overlayElement = null;
    }

    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
  }

  reset() {
    this.hasCompletedTutorial = false;
    localStorage.removeItem('paperclips-tutorial-completed');
  }
}

// Singleton instance
export const tutorialManager = new TutorialManager();
